import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SearchCriteria } from "../../pojo/SearchCriteria";
import { S25SelectComponent } from "../../standalone/s25.select.component";
import { HeadCountOperandOptions } from "./s25ql.const";
import { HeadCountCriterion } from "./s25ql.head.count.criterion.util";

@Component({
    selector: "s25-ng-ql-search-advanced-head-count-criterion",
    host: { class: "head-count-ratio" },
    template: `
        <label class="ngBold">Head Count Ratio</label>
        <div class="head-count-ratio-row">
            <s25-ng-select
                [value]="headCountLeft()"
                (valueChange)="onHeadCountLeftChange($event)"
                [options]="leftOperands()"
                [ariaLabel]="'Left count'"
            />
            <s25-ng-select
                [value]="modeOperator()"
                (valueChange)="onHeadCountOperatorChange($event)"
                [options]="modeOperators"
                [ariaLabel]="'Operator'"
            />
            @if (isRatioMode()) {
                <s25-ng-select
                    [value]="headCountRight()"
                    (valueChange)="onHeadCountRightChange($event)"
                    [options]="rightOperands()"
                    [ariaLabel]="'Ratio denominator'"
                />
                <s25-ng-select
                    [value]="headCountOperator()"
                    (valueChange)="onHeadCountRatioOperatorChange($event)"
                    [options]="comparisonOperators"
                    [ariaLabel]="'Threshold operator'"
                />
                <span class="head-count-ratio-threshold">
                    <input
                        class="c-input"
                        type="number"
                        step="1"
                        [(ngModel)]="stepParam().head_count_ratio_pct"
                        (ngModelChange)="notifyChanged()"
                        aria-label="Percentage threshold"
                    />%
                </span>
            } @else {
                <s25-ng-select
                    [value]="headCountRight()"
                    (valueChange)="onHeadCountRightChange($event)"
                    [options]="rightOperands()"
                    [ariaLabel]="'Right count'"
                />
            }
        </div>
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .head-count-ratio-row {
            display: flex;
            column-gap: 0.5rem;
            align-items: center;
            flex-wrap: wrap;
        }

        .head-count-ratio-row label,
        .head-count-ratio-row span {
            width: auto;
            display: inline;
            margin-bottom: 0;
        }

        :host s25-ng-select {
            width: auto;
            display: inline-block;
            margin-bottom: 0;
        }

        :host s25-ng-select select {
            width: auto;
            display: inline-block;
            margin-bottom: 0;
        }

        .head-count-ratio-threshold input {
            width: 3.5rem;
            display: inline-block;
            margin-inline-end: 0.25rem;
            margin-bottom: 0;
            -moz-appearance: textfield;

            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25SelectComponent, FormsModule],
})
export class S25QLSearchAdvancedHeadCountCriterionComponent {
    readonly stepParam = input.required<SearchCriteria.StepParam>();
    readonly changed = output<void>();

    protected readonly operands = [...HeadCountOperandOptions];
    protected readonly modeOperators = [
        { value: "lt", label: "<" },
        { value: "le", label: "≤" },
        { value: "gt", label: ">" },
        { value: "ge", label: "≥" },
        { value: "eq", label: "=" },
        { value: "ratio", label: ":" },
    ];
    protected readonly comparisonOperators = [
        { value: "lt", label: "<" },
        { value: "le", label: "≤" },
        { value: "gt", label: ">" },
        { value: "ge", label: "≥" },
        { value: "eq", label: "=" },
    ];

    readonly headCountLeft = linkedSignal(() => this.stepParam().head_count_left);
    readonly headCountRight = linkedSignal(() => this.stepParam().head_count_right);
    readonly headCountRatioPct = linkedSignal(() => this.stepParam().head_count_ratio_pct);
    readonly headCountOperator = linkedSignal(() => this.stepParam().head_count_operator);

    protected readonly isRatioMode = computed(() => this.headCountRatioPct() !== undefined);
    protected readonly modeOperator = computed(() => (this.isRatioMode() ? "ratio" : this.headCountOperator()));
    protected readonly leftOperands = computed(() =>
        HeadCountCriterion.operandsForSide(this.operands, this.headCountRight()),
    );
    protected readonly rightOperands = computed(() =>
        HeadCountCriterion.operandsForSide(this.operands, this.headCountLeft()),
    );

    onHeadCountLeftChange(operand: string) {
        this.headCountLeft.set(operand);
        this.stepParam().head_count_left = operand;
        HeadCountCriterion.ensureDistinctOperands(this.stepParam(), "left");
        this.headCountRight.set(this.stepParam().head_count_right);
        this.notifyChanged();
    }

    onHeadCountRightChange(operand: string) {
        this.headCountRight.set(operand);
        this.stepParam().head_count_right = operand;
        HeadCountCriterion.ensureDistinctOperands(this.stepParam(), "right");
        this.headCountLeft.set(this.stepParam().head_count_left);
        this.notifyChanged();
    }

    onHeadCountOperatorChange(operator: string) {
        const stepParam = this.stepParam();
        if (operator === "ratio") {
            stepParam.head_count_ratio_pct ??= 50;
            stepParam.head_count_operator ??= "lt";
        } else {
            delete stepParam.head_count_ratio_pct;
            stepParam.head_count_operator = operator;
        }
        this.headCountRatioPct.set(stepParam.head_count_ratio_pct);
        this.headCountOperator.set(stepParam.head_count_operator);
        this.notifyChanged();
    }

    onHeadCountRatioOperatorChange(operator: string) {
        this.headCountOperator.set(operator);
        this.stepParam().head_count_operator = operator;
        this.notifyChanged();
    }

    notifyChanged() {
        this.changed.emit();
    }
}
